import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  Hidden,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Theme,
  Typography,
  alpha,
  makeStyles,
} from '@material-ui/core'
import { ArrowForward, Visibility, VisibilityOff } from '@material-ui/icons'
import Alert from '@material-ui/lab/Alert'
import classNames from 'classnames'
import { ContentDivider } from 'components/Divider'
import Logo from 'components/Logo'
import Page from 'components/Page'
import { SessionContext } from 'contexts/SessionContext'
import { User } from 'models/User'
import React, { Fragment, useCallback, useContext, useEffect, useState } from 'react'
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from 'react-google-recaptcha-v3'
import { useTranslation } from 'react-i18next'
import { NavLink, useHistory } from 'react-router-dom'
import Api from 'utils/Api'
import Env from 'utils/Env'

export const LoginPage: React.FunctionComponent = () => {
  const classes = useLoginStyles()
  const { t } = useTranslation() // DIPERBAIKI: Hook dikembalikan karena t() dipakai di bawah

  // DIPERBAIKI: Gunakan variabel JSX biasa, JANGAN buat fungsi komponen di dalam komponen.
  // Ini mencegah React melakukan unmount/remount DOM yang sering bikin layar blank.
  const pageContent = (
    <Page title="Sign In">
      <div className={classes.container}>
        <Hidden smDown>
          <div className={classes.header}>
            <p className={classes.headerLogo}>
              <Logo size="large" variant="dark" />
              <h1 className={classes.headerTagline}>{t('app.tagline')}</h1>
            </p>
          </div>
        </Hidden>
        <div className={classes.wrapper}>
          <LoginForm
            onLoginSucceed={() => {
              // login(values)
            }}
          />
        </div>
      </div>
    </Page>
  )

  // Conditional rendering yang aman
  if (Env.nodeEnv === 'development') {
    return pageContent
  }

  return (
    <GoogleReCaptchaProvider reCaptchaKey={(Env.recaptchaKey as string) || ''}>
      {pageContent}
    </GoogleReCaptchaProvider>
  )
}

const LoginForm: React.FunctionComponent<{
  onLoginSucceed: () => void
}> = ({ onLoginSucceed }) => {
  const [session, sessionDispatch] = useContext(SessionContext)
  const { t } = useTranslation()
  const history = useHistory()
  const classes = useLoginStyles()
  const { executeRecaptcha } = useGoogleReCaptcha()

  const [username, setUsername] = useState<string>('')
  const [password, setPassword] = useState<string>('')
  const [showPassword, setShowPassword] = useState<boolean>(false)
  const [error, setError] = useState<string | undefined>()
  const [loading, setLoading] = useState<boolean>(false)

  const login = useCallback(
    async (username: string, password: string, token: string) => {
      try {
        const { data } = await Api.request<{ id: string; token: string }>({
          method: 'POST',
          url: '/users/login',
          data: {
            email: username,
            password,
            // Di development pakai token dummy, di production pakai token recaptcha
            token: Env.nodeEnv === 'development' ? 'xxxxxx' : token,
          },
        })

        if (!data) return

        sessionDispatch({ type: 'SET_ACCESS_TOKEN', token: data.token })

        const { data: userData } = await Api.request<User>({
          method: 'GET',
          url: '/users/me',
        })

        sessionDispatch({ type: 'SET_USER', user: userData })
      } catch (err: any) {
        const errorMessage = err?.response?.data?.error?.message || 'Access forbidden'
        setError(errorMessage)
        throw err // Lempar error agar handleSubmit tahu bahwa proses login gagal
      }
    },
    [sessionDispatch]
  )

  useEffect(() => {
    if (session.user) {
      history.replace('/admin')
    }
  }, [session.user, history])

  const handleClickShowPassword = useCallback(() => {
    setShowPassword((prev) => !prev)
  }, [])

  const handleSubmit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault()
      setError(undefined)
      setLoading(true)

      try {
        let captchaData = 'xxxxxx'


        if (Env.nodeEnv !== 'development') {
          const token = await executeRecaptcha?.('login')
          if (!token) {
            setError(t('errors.captcha_error'))
            setLoading(false)
            return
          }
          captchaData = token
        }

        await login(username, password, captchaData)
        onLoginSucceed()
      } catch (err: any) {
        if (err?.response?.data?.error?.message) {
          setError(err.response.data.error.message)
        } else if (Env.nodeEnv !== 'development') {
          setError(t('errors.captcha_error'))
        }
      } finally {
        setLoading(false)
      }
    },
    [username, password, executeRecaptcha, login, onLoginSucceed, t]
  )

  return (
    <Fragment>
      <Hidden mdUp>
        <Box ml={2}>
          <Logo variant="dark" />
        </Box>
      </Hidden>

      <Paper className={classNames(classes.paperWrap, classes.petal)}>
        <Hidden smDown>
          <div className={classes.topBar}>
            <Grid container justifyContent="space-between">
              <Grid item>
                <NavLink to="/admin" className={classes.brand}>
                  <Logo />
                </NavLink>
              </Grid>
            </Grid>
          </div>
        </Hidden>

        <Box marginY={5}>
          <Typography variant="h4" className={classes.title} gutterBottom>
            {t('login.title')}
          </Typography>
        </Box>

        <section className={classes.socmedSideLogin}>
          <div className={classes.btnArea}></div>
          <ContentDivider content={t('login.sign_in_email')} />
        </section>

        <section className={classes.formWrap}>
          <form onSubmit={handleSubmit}>
            <div>
              <FormControl className={classes.formControl}>
                <TextField
                  variant="outlined"
                  name="username"
                  placeholder={t('login.email')}
                  autoComplete="off"
                  label={t('login.email')}
                  required
                  className={classes.field}
                  disabled={loading}
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                />
              </FormControl>
            </div>
            <div>
              <FormControl className={classes.formControl}>
                <TextField
                  variant="outlined"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  label={t('login.password')}
                  required
                  className={classes.field}
                  disabled={loading}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="Toggle password visibility"
                          onClick={handleClickShowPassword}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </FormControl>
            </div>

            {error && <Alert severity="error">{error}</Alert>}

            <div className={classes.btnArea}>
              <Button
                variant="contained"
                color="primary"
                size="large"
                type="submit"
                disabled={loading}
              >
                {t('login.sign_in')}
                {loading ? (
                  <CircularProgress size={20} className={classNames(classes.rightIcon, classes.iconSmall)} />
                ) : (
                  <ArrowForward className={classNames(classes.rightIcon, classes.iconSmall)} />
                )}
              </Button>
            </div>
          </form>
        </section>
      </Paper>
    </Fragment>
  )
}

const wrapper = (theme: Theme, opacity: number) => ({
  padding: theme.spacing(3),
  backgroundColor: alpha(theme.palette.background.paper, opacity),
  backgroundRepeat: 'no-repeat',
  color: theme.palette.text.primary,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  backgroundAttachment: 'fixed',
})

export const useLoginStyles = makeStyles((theme: Theme) => ({
  root: {
    display: 'flex',
    alignItems: 'stretch',
    minHeight: '100%',
  },
  container: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    minHeight: '100%',
    height: '100%',
    background: `linear-gradient(125deg, rgba(152, 204, 144, 1) 21%, rgba(93, 157, 83, 1) 51%, rgba(67, 120, 59, 1) 77%) !important`,
  },
  header: {
    color: '#fff',
    justifyContent: 'center',
    flex: 1,
    padding: 50,
  },
  headerLogo: {
    marginTop: 180,
    marginBottom: 180,
  },
  headerLogoImg: {
    width: 260,
  },
  headerTitle: {
    fontSize: 48,
  },
  headerTagline: {
    fontSize: 32,
    marginTop: '2rem',
    marginLeft: '1rem',
  },
  headerSubtitle: {
    fontSize: 32,
    marginTop: 15,
    marginBottom: 15,
  },
  headerDesc: {
    marginTop: 15,
    fontSize: 18,
  },
  wrapper: {
    minWidth: 320,
    height: '100%',
    [theme.breakpoints.down('md')]: {
      flex: 1,
    },
  },
  topBar: {
    display: 'flex',
    justifyContent: 'space-between',
    '& $icon': {
      marginRight: theme.spacing(1),
    },
    [theme.breakpoints.down('sm')]: {
      justifyContent: 'center',
      marginBottom: theme.spacing(3),
      '& a': {
        display: 'none',
      },
    },
  },
  socmedSideLogin: {
    padding: '16px 16px 1px', // DIPERBAIKI: Typo '16' -> '16px'
    margin: '0 auto',
    '& button': {
      padding: '4px 16px',
      margin: `0 ${theme.spacing(1)}px`,
    },
    [theme.breakpoints.only('sm')]: {
      width: 480,
    },
  },
  brand: {
    display: 'flex',
    alignItems: 'center',
    position: 'relative',
    fontSize: 16,
    fontWeight: 500,
    color: theme.palette.text.primary,
    textDecoration: 'none',
    '&$outer': {
      color: theme.palette.common.white,
    },
    [theme.breakpoints.down('md')]: {
      margin: theme.spacing(2),
    },
    '& img': {
      width: 30,
      marginRight: 10,
    },
  },
  outer: {},
  paperWrap: {
    ...wrapper(theme, 1),
    height: '100%',
    borderRadius: 0,
    [theme.breakpoints.down('md')]: {
      padding: 10,
    },
    [theme.breakpoints.up('lg')]: {
      width: 560,
    },
    '& $topBar': {
      marginBottom: theme.spacing(4),
    },
    textAlign: 'center',
  },
  petal: {},
  formControl: {
    width: '100%',
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  btnArea: {
    display: 'flex',
    justifyContent: 'space-around',
    margin: `${theme.spacing(2)}px 0`,
    fontSize: 12,
    '& $label': {
      fontSize: 12,
      '& span': {
        fontSize: 12,
      },
    },
    '& button': {
      margin: `0 ${theme.spacing(1)}px`,
    },
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
      '& button': {
        width: '100%',
        margin: 5,
      },
    },
  },
  buttonLink: {
    background: 'none',
    padding: 0,
    textTransform: 'none',
    transition: 'color ease 0.3s',
    fontWeight: theme.typography.fontWeightRegular as any,
    textDecoration: 'none',
    justifyContent: 'center',
    alignItems: 'center',
    fontSize: '0.875rem',
    '&:hover': {
      background: 'none',
      color: theme.palette.secondary.main,
    },
  },
  leftIcon: {
    marginRight: theme.spacing(1),
  },
  rightIcon: {
    marginLeft: theme.spacing(1),
  },
  iconSmall: {
    fontSize: 20,
  },
  icon: {},
  optArea: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: `0 ${theme.spacing(0.5)}px`,
  },
  title: {
    color: theme.palette.primary.main,
  },
  subtitle: {
    fontSize: 14,
  },
  formWrap: {
    margin: '0 auto',
    [theme.breakpoints.only('sm')]: {
      width: 480,
    },
  },
  field: {},
  label: {},
}))